import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Clock, ArrowLeft, Download, Calendar, CheckCircle, ArrowRight, ChevronDown } from 'lucide-react';
import { FAQSchema, ArticleSchema, BreadcrumbSchema } from '@/components/SEOSchema';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LeadMagnetCTA({ text, isAr }) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await axios.post(`${API}/subscribers`, { email, source: 'blog_cta', lead_magnet: text });
      setStatus('success');
    } catch { setStatus('error'); }
  };

  if (status === 'success') {
    return (
      <div data-testid="lead-magnet-success" className="my-8 bg-[#1A7A4A]/5 border border-[#1A7A4A]/20 rounded-xl p-6 text-center">
        <CheckCircle className="w-10 h-10 text-[#1A7A4A] mx-auto mb-3" />
        <p className="text-[#1A7A4A] font-semibold text-sm">{isAr ? 'تم! سيصلك الملف على بريدك الإلكتروني خلال دقائق.' : 'Done! The file will be sent to your email shortly.'}</p>
      </div>
    );
  }

  return (
    <div data-testid="lead-magnet-cta" className="my-8 bg-gradient-to-br from-[#0B2F44] to-[#0D3A54] rounded-xl p-6 lg:p-8">
      <div className="flex items-start gap-3 mb-4">
        <Download className="w-6 h-6 text-[#C9A84C] flex-shrink-0 mt-0.5" />
        <p className="text-white text-sm font-medium leading-relaxed">{text}</p>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
        <Input
          data-testid="lead-magnet-email"
          type="email"
          placeholder={isAr ? 'أدخل بريدك الإلكتروني' : 'Enter your email'}
          value={email}
          onChange={e => setEmail(e.target.value)}
          className="h-11 bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#C9A84C] flex-1"
          required
        />
        <Button type="submit" className="bg-[#C9A84C] hover:bg-[#A68966] text-[#0B2F44] font-bold px-6 h-11 whitespace-nowrap">
          <Download className="w-4 h-4 me-1" /> {isAr ? 'حمّل مجاناً' : 'Download Free'}
        </Button>
      </form>
    </div>
  );
}

function ConsultationCTA({ text, isAr }) {
  return (
    <div data-testid="consultation-cta" className="my-8 bg-[#C9A84C]/5 border border-[#C9A84C]/20 rounded-xl p-6 lg:p-8 text-center">
      <Calendar className="w-8 h-8 text-[#C9A84C] mx-auto mb-3" />
      <p className="text-[#0B2F44] font-medium text-sm mb-4">{text}</p>
      <Link to="/contact">
        <Button className="bg-[#C9A84C] hover:bg-[#A68966] text-[#0B2F44] font-bold px-8">
          {isAr ? 'احجز استشارة مجانية' : 'Book Free Consultation'}
          <ArrowRight className="w-4 h-4 ms-2" />
        </Button>
      </Link>
    </div>
  );
}

export default function BlogArticlePage() {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const displayFont = isAr ? 'var(--font-ar-display)' : 'var(--font-en-display)';
  const bodyFont = isAr ? 'var(--font-ar-body)' : 'var(--font-en-body)';
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/blog/articles/${slug}`).then(r => {
      setArticle(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFB] flex items-center justify-center pt-20">
        <div className="animate-spin w-8 h-8 border-4 border-[#A68966] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#F8FAFB] flex items-center justify-center pt-20">
        <div className="text-center">
          <h2 className="text-xl font-bold text-[#0B2F44] mb-2">{isAr ? 'المقال غير موجود' : 'Article Not Found'}</h2>
          <Link to="/blog" className="text-[#A68966] hover:underline">{isAr ? 'العودة للمدونة' : 'Back to Blog'}</Link>
        </div>
      </div>
    );
  }

  const content = isAr ? (article.content_ar || []) : (article.content_en || []);
  const faqs = isAr ? (article.faqs_ar || []) : (article.faqs_en || []);
  const execSummary = isAr ? article.executive_summary_ar : article.executive_summary_en;

  return (
    <div data-testid="blog-article-page">
      {/* Schema Markup */}
      <ArticleSchema article={article} lang={lang} />
      <BreadcrumbSchema items={[{ name: isAr ? 'الرئيسية' : 'Home', path: '/' }, { name: isAr ? 'المدونة' : 'Blog', path: '/blog' }, { name: isAr ? article.title_ar : article.title_en, path: `/blog/${article.slug}` }]} />
      {faqs.length > 0 && <FAQSchema faqs={faqs} />}
      {/* Hero */}
      <section className="relative h-[50vh] min-h-[400px]">
        <img src={article.image} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#071E2C] via-[#071E2C]/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-12">
          <div className="max-w-[900px] mx-auto">
            <Link to="/blog" className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm mb-4 transition-colors">
              <ArrowLeft className="w-4 h-4" /> {isAr ? 'العودة للمدونة' : 'Back to Blog'}
            </Link>
            <Badge className="bg-[#C9A84C]/20 text-[#C9A84C] border-0 mb-3 text-xs">
              {isAr ? article.category_ar : article.category_en}
            </Badge>
            <h1
              data-testid="article-title"
              className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4"
              style={{ fontFamily: displayFont, lineHeight: isAr ? '1.5' : '1.2' }}
            >
              {isAr ? article.title_ar : article.title_en}
            </h1>
            <div className="flex items-center gap-4 text-sm text-white/50">
              <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {article.read_time} min</span>
              <span>{article.author}</span>
              <span>{article.published_at}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="bg-[#F8FAFB] py-12 lg:py-16">
        <div className="max-w-[900px] mx-auto px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 lg:p-12 border border-[#D0D8DC]/50 shadow-sm">
            {/* Executive Summary - GEO Optimized */}
            {execSummary && (
              <div className="mb-8 bg-[#F8FAFB] rounded-xl p-6 border-s-4 border-[#C9A84C]" data-testid="executive-summary">
                <p className="text-xs text-[#A68966] font-semibold uppercase tracking-wider mb-2">{isAr ? 'خلاصة سريعة' : 'Quick Summary'}</p>
                <p className="text-[#0B2F44] text-sm font-medium leading-relaxed" style={{ lineHeight: isAr ? '2' : '1.7' }}>
                  {execSummary}
                </p>
              </div>
            )}
            {content.map((block, i) => {
              if (block.type === 'heading') {
                return (
                  <h2 key={i} className="text-xl lg:text-2xl font-bold text-[#0B2F44] mt-10 mb-4" style={{ fontFamily: displayFont }}>
                    {block.text}
                  </h2>
                );
              }
              if (block.type === 'paragraph') {
                return (
                  <p key={i} className="text-[#1A2B35] text-base mb-5 leading-relaxed" style={{ fontFamily: bodyFont, lineHeight: isAr ? '2' : '1.8' }}>
                    {block.text}
                  </p>
                );
              }
              if (block.type === 'table') {
                return (
                  <div key={i} className="my-8 overflow-x-auto rounded-xl border border-[#D0D8DC]/50" data-testid={`article-table-${i}`}>
                    <table className="w-full">
                      <thead><tr className="bg-[#0B2F44]">
                        {block.headers.map((h, hi) => <th key={hi} className="px-4 py-3 text-start text-xs font-semibold text-white uppercase tracking-wider">{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {block.rows.map((row, ri) => (
                          <tr key={ri} className={`border-b border-[#D0D8DC]/20 ${ri % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFB]'}`}>
                            {row.map((cell, ci) => <td key={ci} className="px-4 py-3 text-sm text-[#1A2B35]">{cell}</td>)}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              }
              if (block.type === 'numbered_list') {
                return (
                  <ol key={i} className="my-6 space-y-4">
                    {block.items.map((item, li) => (
                      <li key={li} className="flex gap-4">
                        <span className="flex-shrink-0 w-8 h-8 rounded-full bg-[#0B2F44] text-white flex items-center justify-center text-sm font-bold">{li + 1}</span>
                        <p className="text-[#1A2B35] text-sm leading-relaxed pt-1" style={{ lineHeight: isAr ? '2' : '1.7' }}>{item}</p>
                      </li>
                    ))}
                  </ol>
                );
              }
              if (block.type === 'stat') {
                return (
                  <div key={i} className="my-8 bg-[#0B2F44] rounded-xl p-6 text-center" data-testid={`article-stat-${i}`}>
                    <div className="text-3xl lg:text-4xl font-black text-[#C9A84C] mb-1" style={{ fontFamily: 'var(--font-en-mono)' }}>{block.value}</div>
                    <p className="text-white/60 text-sm">{block.label}</p>
                  </div>
                );
              }
              if (block.type === 'cta') {
                if (block.cta_type === 'lead_magnet') {
                  return <LeadMagnetCTA key={i} text={block.text} isAr={isAr} />;
                }
                if (block.cta_type === 'consultation') {
                  return <ConsultationCTA key={i} text={block.text} isAr={isAr} />;
                }
              }
              return null;
            })}

            {/* FAQ Section */}
            {faqs.length > 0 && (
              <div className="mt-12 pt-8 border-t border-[#D0D8DC]/30" data-testid="article-faq">
                <h2 className="text-xl font-bold text-[#0B2F44] mb-6" style={{ fontFamily: displayFont }}>
                  {isAr ? 'الأسئلة الشائعة' : 'Frequently Asked Questions'}
                </h2>
                <div className="space-y-3">
                  {faqs.map((faq, fi) => (
                    <FAQItem key={fi} question={faq.question} answer={faq.answer} />
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Bottom email capture */}
          <div className="mt-12 bg-[#0B2F44] rounded-2xl p-8 lg:p-10 text-center">
            <h3 className="text-xl font-bold text-white mb-2" style={{ fontFamily: displayFont }}>
              {isAr ? 'لا تفوّت أي تحليل جديد' : "Don't Miss Any New Insights"}
            </h3>
            <p className="text-white/50 text-sm mb-6">
              {isAr ? 'اشترك للحصول على تحليلات حصرية وفرص استثمارية مباشرة في بريدك.' : 'Subscribe for exclusive analysis and investment opportunities delivered to your inbox.'}
            </p>
            <EmailCapture isAr={isAr} />
          </div>
        </div>
      </section>
    </div>
  );
}

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#D0D8DC]/50 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-start hover:bg-[#F8FAFB] transition-colors">
        <span className="font-semibold text-[#0B2F44] text-sm pe-4">{question}</span>
        <ChevronDown className={`w-5 h-5 text-[#4A6572] flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 -mt-1">
          <p className="text-[#4A6572] text-sm leading-relaxed">{answer}</p>
        </div>
      )}
    </div>
  );
}

function EmailCapture({ isAr }) {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      await axios.post(`${API}/subscribers`, { email, source: 'blog_subscribe' });
      setDone(true);
    } catch { setDone(true); }
  };
  if (done) return <p className="text-[#C9A84C] font-semibold text-sm">{isAr ? 'تم الاشتراك بنجاح!' : 'Subscribed successfully!'}</p>;
  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto" data-testid="blog-email-capture">
      <Input type="email" placeholder={isAr ? 'بريدك الإلكتروني' : 'Your email'} value={email} onChange={e => setEmail(e.target.value)} className="h-12 bg-white/10 border-white/20 text-white placeholder:text-white/40" required />
      <Button type="submit" className="bg-[#C9A84C] hover:bg-[#A68966] text-[#0B2F44] font-bold px-6 h-12">{isAr ? 'اشترك الآن' : 'Subscribe'}</Button>
    </form>
  );
}
